import { useEffect, useState, useCallback } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { Loading, EmptyState, StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { CreditCard, Home, Copy, Smartphone, Building, Loader2, CheckCircle2, AlarmClock, FileDown } from "lucide-react";
import { ReceiptButton } from "@/components/ReceiptButton";
import { AutopayCard } from "@/components/AutopayCard";
import { RentNudgeBanner } from "@/components/RentNudgeBanner";
import { toast } from "sonner";

function CopyRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-500">{label}</span>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
        className="text-sm font-semibold text-slate-900 flex items-center gap-1.5 hover:text-primary">
        {value} <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function TenantPayments() {
  const [data, setData] = useState(null);
  const [pays, setPays] = useState([]);
  const [paying, setPaying] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "zelle", reference: "" });

  const load = useCallback(() => {
    api.get("/tenant/overview").then((r) => setData(r.data));
    api.get("/tenant/payments").then((r) => setPays(r.data)).catch((e) => console.warn("payments load failed", e?.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loading />;
  if (!data.lease)
    return <EmptyState icon={Home} title="No active lease" subtitle="Payment options appear once your landlord assigns you a lease." />;

  const cur = data.current;
  const inst = data.instructions || {};
  const period = cur?.period;
  const balance = cur?.tenant_balance || 0;
  const hasFees = data.ledger.some((r) => r.fees > 0);

  const payCard = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post("/payments/checkout", { lease_id: data.lease.id, period, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setPaying(false); }
  };

  const recordManual = async () => {
    if (!form.amount) return toast.error("Enter the amount you paid");
    try {
      await api.post("/payments", { lease_id: data.lease.id, period, amount: Number(form.amount), source: "tenant", method: form.method, reference: form.reference, note: "Reported by tenant" });
      toast.success("Payment reported — your landlord will confirm it");
      setRecordOpen(false); setForm({ amount: "", method: "zelle", reference: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Pay rent</h1>
      <p className="text-sm text-slate-500 mb-7">{cur?.label} · {data.lease.property?.name}</p>

      <RentNudgeBanner nudge={data.nudge} leaseId={data.lease.id} showOther={false} />

      {/* Balance hero */}
      <div className="rounded-2xl bg-slate-900 text-white p-6 mb-5 overflow-hidden relative">
        <div className="relative z-10">
          <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Your copay balance · {cur?.label}</div>
          <div className="text-4xl font-extrabold font-display mt-2">{fmtMoney(balance)}</div>
          <div className="text-sm text-white/70 mt-1">of {fmtMoney(cur?.tenant_due || 0)} due · {fmtMoney(cur?.tenant_paid || 0)} paid</div>
          {cur?.fees > 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-rose-500/20 text-rose-200 px-3 py-1.5 text-sm font-semibold mt-3" data-testid="tenant-fee-note">
              <AlarmClock className="h-4 w-4" /> Includes {fmtMoney(cur.fees)} in late fees
            </div>
          )}
          {balance <= 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/20 text-emerald-300 px-3 py-1.5 text-sm font-semibold mt-3">
              <CheckCircle2 className="h-4 w-4" /> Fully paid this month
            </div>
          )}
        </div>
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-500/20 blur-2xl" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-2"><span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "#6366f120", color: "#6366f1" }}><CreditCard className="h-5 w-5" /></span><h2 className="font-display font-bold text-lg text-slate-900">Pay by card</h2></div>
          <p className="text-sm text-slate-500 mb-5">Instant & secure via Stripe. Automatically recorded in your ledger.</p>
          <Button onClick={payCard} disabled={paying || balance <= 0} className="w-full gap-2 h-11" data-testid="pay-card-btn">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {balance > 0 ? `Pay ${fmtMoney(balance)} now` : "Nothing due"}
          </Button>
          <p className="text-[11px] text-slate-400 mt-3 text-center">Test card: 4242 4242 4242 4242 · any future date · any CVC</p>
        </div>

        {/* Manual */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Pay by Zelle, CashApp or Chase</h2>
          <p className="text-sm text-slate-500 mb-4">Send to your landlord, then report it below so they can confirm.</p>
          <div className="rounded-xl border border-slate-200 divide-y px-4">
            <CopyRow label="Zelle" value={inst.zelle} />
            <CopyRow label="CashApp" value={inst.cashapp} />
            <CopyRow label="Chase name" value={inst.chase_name} />
            <CopyRow label="Chase account #" value={inst.chase_account} />
            <CopyRow label="Chase routing #" value={inst.chase_routing} />
          </div>
          {inst.notes && <p className="text-xs text-slate-500 mt-3 bg-slate-50 rounded-lg p-3">{inst.notes}</p>}

          <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
            <DialogTrigger asChild><Button variant="outline" className="w-full mt-4" data-testid="report-payment-btn">I've sent a payment — report it</Button></DialogTrigger>
            <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Report your payment</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Amount sent</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="report-amount" placeholder={String(balance)} /></div>
                <div className="space-y-1.5"><Label>How did you pay?</Label>
                  <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
                    <SelectTrigger data-testid="report-method"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="zelle">Zelle</SelectItem><SelectItem value="cashapp">CashApp</SelectItem><SelectItem value="chase">Chase deposit</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Confirmation # (optional)</Label><Input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} /></div>
              </div>
              <DialogFooter><Button onClick={recordManual} className="w-full" data-testid="report-submit">Report payment</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Auto-pay */}
      <div className="mt-5"><AutopayCard onChange={load} /></div>

      {/* Receipts */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-5" data-testid="tenant-receipts">
        <div className="flex items-center gap-2 mb-1"><FileDown className="h-5 w-5 text-primary" /><h3 className="font-display font-bold text-lg text-slate-900">Receipts</h3></div>
        <p className="text-sm text-slate-500 mb-4">Download a PDF receipt for any confirmed payment.</p>
        {pays.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">No payments recorded yet.</p> : (
          <div className="divide-y divide-slate-100">
            {pays.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`tenant-payment-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{fmtMoney(p.amount)} · {p.period_label}</div>
                  <div className="text-xs text-slate-500 capitalize">{p.method} · {p.source === "section8" ? "Section 8 HAP" : "Your copay"} · {new Date(p.paid_at || p.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusPill status={p.status} />
                  {p.status === "confirmed" ? <ReceiptButton payment={p} /> : <span className="text-[11px] text-slate-400">Receipt after confirmation</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-5">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Your payment history</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
              <tr><th className="text-left font-semibold py-2.5">Month</th><th className="text-right font-semibold py-2.5">Paid</th><th className="text-right font-semibold py-2.5">Due</th>{hasFees && <th className="text-right font-semibold py-2.5">Late fees</th>}<th className="text-right font-semibold py-2.5">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.ledger.map((r) => (
                <tr key={r.period}>
                  <td className="py-3 font-medium text-slate-800">{r.label}</td>
                  <td className="py-3 text-right font-mono text-xs font-semibold">{fmtMoney(r.tenant_paid)}</td>
                  <td className="py-3 text-right font-mono text-xs text-slate-400">{fmtMoney(r.tenant_due)}</td>
                  {hasFees && <td className="py-3 text-right font-mono text-xs">{r.fees > 0 ? <span className="text-rose-600 font-semibold">+{fmtMoney(r.fees)}</span> : <span className="text-slate-300">—</span>}</td>}
                  <td className="py-3 text-right"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
